import {DM_URN_TYPE} from '../constants/attachments';
import {getBlob} from './blob-helpers';
import {ENV_CONFIGS} from '../constants/envs';
import {encodeSafeForUrl} from './base64-helpers';
import {downloadAttachmentContentsToBlob} from './data-transfer-helpers';

export const imageExtensionMimeTypeMapping = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  bmp: 'image/bmp'
};
export const imageExtensions = Object.keys(imageExtensionMimeTypeMapping);
const getExtension = fileName =>
  fileName
    .split('.')
    .pop()
    .toLowerCase();

const isImageFilename = fileName => {
  if (!fileName) {
    return false;
  }
  const extension = getExtension(fileName);
  return imageExtensions.includes(extension);
};

export const getMimeType = url => {
  if (!url) {
    return '';
  } else {
    const extension = getExtension(url);
    return imageExtensionMimeTypeMapping[extension] || '';
  }
};

export const isImageAttachment = attachment => isImageFilename(attachment.name) || isImageFilename(attachment.urn);
export const isDocsAttachment = attachment => attachment.urnType === DM_URN_TYPE;

/**
 * This method returns true if a pushpin can be added to document.
 * We can remove this method once https://git.autodesk.com/BIM360/dm-sdk-js/issues/53 is resolved.
 * @param document
 * @returns {boolean}
 */
export const isSupportPushpin = document => {
  if (isImageFilename(document.fileName)) {
    return false;
  }
  if (!document.currentVersion.bubbleUrn) {
    return false;
  }
  const entityType = document.currentVersion.entityType;
  switch (entityType) {
    case 'DOCUMENT':
      return true;
    case 'SEED_FILE':
      if (document.currentVersion.processState) {
        if (document.currentVersion.processState !== 'PROCESSING_COMPLETE') {
          return false;
        }
        if (document.currentVersion.extractionState === 'SUCCESS') {
          return true;
        }
        if (document.currentVersion.partialExtractionFailure === true) {
          return true;
        }
      }
      return false;
    default:
      return false;
  }
};

const isNotCurrentDocument = (document, currentLinkedUrn) => document.urn !== currentLinkedUrn;

// Filters pushpin supported documents and filter out currently linked document
export const filterAllowedDocuments = (data, currentLinkedUrn) => {
  const result = Object.assign({}, data);
  result.documents = data.documents.filter(d => isSupportPushpin(d) && isNotCurrentDocument(d, currentLinkedUrn));
  return result;
};

// Return an array of file extensions accepted per issue type.
// Empty string means all files are allowed.
export const getAllowedFiles = () => '';

export const fetchImageAttachmentBlob = ({attachment, tokenPromise, projectScope}) => {
  const mimeType = getMimeType(attachment.name); // Files uploaded from the user's computer can only be of type png or jpeg
  return tokenPromise
    .then(token =>
      downloadAttachmentContentsToBlob({
        attachment,
        token,
        projectScope,
        mimeType
      })
    )
    .then(blob => ({blob, urn: attachment.urn}))
    .catch(() => ({urn: attachment.urn}));
};

export const fetchDocumentAttachmentBlob = ({attachment, documentsByUrnsPromise, tokenPromise, projectScope}) =>
  Promise.all([documentsByUrnsPromise, tokenPromise]).then(([documents, token]) => {
    const document = documents[attachment.urn];
    if (document.currentVersion.bubbleUrn) {
      let thumbnailUrl = `${ENV_CONFIGS.APIGEE_HOST}${ENV_CONFIGS.DERIVATIVE_SERVICE}/thumbnails/${encodeSafeForUrl(
        document.currentVersion.bubbleUrn
      )}?type=small`;
      if (document.currentVersion.bubbleViewableGuid) {
        thumbnailUrl += `&guid=${document.currentVersion.bubbleViewableGuid}`;
      }
      return getBlob(thumbnailUrl, 'image/png', token, projectScope)
        .then(blob => ({blob, urn: document.urn}))
        .catch(() => ({urn: document.urn}));
    }

    return {urn: document.urn};
  });
